import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useOutsideClick } from 'src/hooks/utils';
import { useLocation } from 'react-router-dom';
import MenuList from './NavItemList/NavItemList';
import { NavHeader } from './NavHeader/NavHeader';
import { NavItemUser } from './NavItemUser/NavItemUser';
import '../../../styles/mixin.scss';
import styles from './Nav.module.scss';
import NavItem from './NavItem/NavItem';
import { useNavigationItems } from '../../../hooks/useNavigationItems';
import { useBugReport } from '../../../providers/BugReportProvider';

export const Nav = () => {
  const { t } = useTranslation();
  const ref = React.useRef(null);
  const location = useLocation();
  const [mobileMenuActive, setMobileMenuActive] = useState(false);

  const [expanded, setExpanded] = useState(() => {
    const isExpanded = localStorage.getItem('sidebarExpanded');
    return isExpanded === null ? true : isExpanded === '1';
  });

  const { navigationMenuItems } = useNavigationItems();
  const { open: openBugReport } = useBugReport();
  useOutsideClick(ref, () => {
    setMobileMenuActive(false);
    document.body.classList.remove('menu-active');
  });

  const toggleSidebar = () => {
    localStorage.setItem('sidebarExpanded', !expanded ? '1' : '0');
    setExpanded(!expanded);
  };

  useEffect(() => {
    const { body } = document;
    const observer = new MutationObserver(() => {
      const isMobileActive = body.classList.contains('menu-active');
      setMobileMenuActive(isMobileActive);
      if (isMobileActive) {
        setExpanded(true);
      } else {
        setExpanded(localStorage.getItem('sidebarExpanded') !== '0');
      }
    });

    observer.observe(body, { attributes: true });
    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (mobileMenuActive) {
      setMobileMenuActive(false);
      document.body.classList.remove('menu-active');
    }
  }, [location.pathname]);

  return (
    <aside
      ref={ref}
      id="default-sidebar"
      className={classNames(styles.root, expanded || mobileMenuActive ? styles.expanded : styles.collapsed, {
        [styles.mobileMenuActive]: mobileMenuActive,
      })}
      aria-label="Sidebar">
      <div className="h-full flex flex-col justify-between surface-sidebar">
        <NavHeader expanded={expanded} />
        <div className="flex-shrink flex-1 overflow-auto">
          <ul className={expanded ? 'mx-12' : 'mx-14'}>
            <MenuList items={navigationMenuItems} expanded={expanded} />
          </ul>
        </div>
        <div className="border-subtle--dark border-t-1">
          <ul className={classNames({ 'mx-14 pb-12': !expanded, 'mx-12 py-16': expanded })}>
            <li className="mb-2">
              <NavItem
                onClick={() => openBugReport()}
                icon="bug"
                label={t('bug_report.title')}
                expanded={expanded}
                inactive
              />
            </li>
            <li className={classNames({ [styles.rotate]: !expanded })}>
              <NavItem
                onClick={toggleSidebar}
                icon="keyboard-tab-left"
                label={t('navbar.collapse_sidebar')}
                expanded={expanded}
                inactive
              />
            </li>
          </ul>
          <NavItemUser expanded={expanded} />
        </div>
      </div>
    </aside>
  );
};
